import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

// Import API slices
import { authApi } from '../services/authApi';
import { employeeApi } from '../services/employeeApi';
import { departmentApi } from '../services/departmentApi';
import { designationApi } from '../services/designationApi';
import { branchApi } from '../services/branchApi';
import { planApi } from '../services/planApi';
import { onboardingApi } from '../services/onboardingApi';
import { attendanceApi } from '../services/attendanceApi';
import { leaveApi } from '../services/leaveApi';
import { loanApi } from '../services/loanApi';
import { assetApi } from '../services/assetApi';
import { letterApi } from '../services/letterApi';
import { companyApi } from '../services/companyApi';
import { subscriptionApi } from '../services/subscriptionApi';
import { documentApi } from '../services/documentApi';
import { shiftApi } from '../services/shiftApi';
import { taxApi } from '../services/taxApi';
import { salaryStructureApi } from '../services/salaryStructureApi';
import { holidayApi } from '../services/holidayApi';
import { notificationApi } from '../services/notificationApi';
import { payrollApi } from '../services/payrollApi';
import { tdsReportApi } from '../services/tdsReportApi';
import { reportsApi } from '../services/reportsApi';
import { taskApi } from '../services/taskApi';
import { superAdminApi } from './api/superAdminApi';
import { publicApi } from './api/publicApi';
import { dashboardApi } from './api/dashboardApi';

// Import feature slices
import authSlice from './slices/authSlice';
import uiSlice from './slices/uiSlice';
import employeeSlice from './slices/employeeSlice';
import departmentSlice from './slices/departmentSlice';
import designationSlice from './slices/designationSlice';
import branchSlice from './slices/branchSlice';
import attendanceSlice from './slices/attendanceSlice';
import leaveSlice from './slices/leaveSlice';
import loanSlice from './slices/loanSlice';
import assetSlice from './slices/assetSlice';
import shiftSlice from './slices/shiftSlice';
import taxSlice from './slices/taxSlice';
import notificationsReducer from './slices/notificationSlice';
import payrollSlice from './slices/payrollSlice';
import taskSlice from './slices/taskSlice';

export const store = configureStore({
  reducer: {
    // Feature slices
    auth: authSlice,
    ui: uiSlice,
    employee: employeeSlice,
    department: departmentSlice,
    designation: designationSlice,
    branch: branchSlice,
    attendance: attendanceSlice,
    leave: leaveSlice,
    loan: loanSlice,
    asset: assetSlice,
    shift: shiftSlice,
    tax: taxSlice,
    notifications: notificationsReducer,
    payroll: payrollSlice,
    tasks: taskSlice,

    // API slices
    [authApi.reducerPath]: authApi.reducer,
    [employeeApi.reducerPath]: employeeApi.reducer,
    [departmentApi.reducerPath]: departmentApi.reducer,
    [designationApi.reducerPath]: designationApi.reducer,
    [branchApi.reducerPath]: branchApi.reducer,
    [planApi.reducerPath]: planApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    [attendanceApi.reducerPath]: attendanceApi.reducer,
    [leaveApi.reducerPath]: leaveApi.reducer,
    [loanApi.reducerPath]: loanApi.reducer,
    [assetApi.reducerPath]: assetApi.reducer,
    [letterApi.reducerPath]: letterApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [subscriptionApi.reducerPath]: subscriptionApi.reducer,
    [documentApi.reducerPath]: documentApi.reducer,
    [shiftApi.reducerPath]: shiftApi.reducer,
    [taxApi.reducerPath]: taxApi.reducer,
    [salaryStructureApi.reducerPath]: salaryStructureApi.reducer,
    [holidayApi.reducerPath]: holidayApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [payrollApi.reducerPath]: payrollApi.reducer,
    [tdsReportApi.reducerPath]: tdsReportApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [taskApi.reducerPath]: taskApi.reducer,
    [superAdminApi.reducerPath]: superAdminApi.reducer,
    [publicApi.reducerPath]: publicApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
  },
  
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat(
      authApi.middleware,
      employeeApi.middleware,
      departmentApi.middleware,
      designationApi.middleware,
      branchApi.middleware,
      planApi.middleware,
      onboardingApi.middleware,
      attendanceApi.middleware,
      leaveApi.middleware,
      loanApi.middleware,
      assetApi.middleware,
      letterApi.middleware,
      companyApi.middleware,
      subscriptionApi.middleware,
      documentApi.middleware,
      shiftApi.middleware,
      taxApi.middleware,
      salaryStructureApi.middleware,
      holidayApi.middleware,
      notificationApi.middleware,
      payrollApi.middleware,
      tdsReportApi.middleware,
      reportsApi.middleware,
      taskApi.middleware,
      superAdminApi.middleware,
      publicApi.middleware,
      dashboardApi.middleware
    ),
  
  devTools: process.env.NODE_ENV !== 'production',
});

// Enable refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);
